import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, LinearProgress } from '@mui/material';
import { useAuth } from '../../../../contexts/AuthContext';
import { listenMockupApp } from '../services/mockupService';
import { APPLAB_ACCENT, APPLAB_ORANGE } from '../../constants';

const STEPS = [
  { label: 'Analyserar brief och kontext…',     delay: 0 },
  { label: 'Skapar UI-struktur och layout…',     delay: 4000 },
  { label: 'Bygger sektioner med realistisk data…', delay: 9000 },
  { label: 'Förfinar design och interaktioner…', delay: 15000 },
  { label: 'Avslutar och publicerar…',           delay: 21000 },
];

export default function MockupGenerating() {
  const { appId }        = useParams();
  const location         = useLocation();
  const navigate         = useNavigate();
  const { currentUser }  = useAuth();

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError]           = useState(null);
  const [progress, setProgress]     = useState(0);
  const calledRef                   = useRef(false);
  const unsubRef                    = useRef(null);
  const progressRef                 = useRef(null);

  // Animated progress bar
  useEffect(() => {
    const start = Date.now();
    const DURATION = 28000; // match max expected generation time
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.min(95, (elapsed / DURATION) * 100));
    }, 250);
    return () => clearInterval(progressRef.current);
  }, []);

  // Step labels animation
  useEffect(() => {
    const timers = STEPS.slice(1).map((s, i) =>
      setTimeout(() => setActiveStep(i + 1), s.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  // Listen to Firestore for completion
  useEffect(() => {
    if (!appId) return;
    unsubRef.current = listenMockupApp(appId, (app) => {
      if (app.status === 'done') {
        clearInterval(progressRef.current);
        setProgress(100);
        setTimeout(() => navigate(`/applab/mockup/${appId}/edit`), 400);
      } else if (app.status === 'error') {
        clearInterval(progressRef.current);
        setError(app.error_msg || 'Något gick fel under genereringen.');
      }
    });
    return () => unsubRef.current?.();
  }, [appId, navigate]);

  // Trigger generation (exactly once)
  useEffect(() => {
    if (calledRef.current || !currentUser || !appId) return;
    calledRef.current = true;

    const draft = location.state?.draft;
    if (!draft) {
      setError('Draft saknas. Gå tillbaka och försök igen.');
      return;
    }

    currentUser.getIdToken().then(token =>
      fetch('/.netlify/functions/applab-mockup-generate-background', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ appId, draft }),
      })
    ).then(async (res) => {
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(text);
      }
      // Firestore listener handles the navigation on success
    }).catch((e) => {
      setError(e.message || 'Nätverksfel – försök igen.');
      clearInterval(progressRef.current);
    });
  }, [currentUser, appId, location.state]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#111', color: 'white', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', borderBottom: '1px solid rgba(255,255,255,0.06)', px: 3, py: 2 }}>
        <Typography fontWeight={700} fontSize="0.9rem">
          App<span style={{ color: APPLAB_ORANGE }}>LAB</span>
          <span style={{ color: '#555', fontWeight: 400 }}> · MockupN</span>
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
        <Box sx={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>
          {error ? (
            <>
              <Typography fontSize="2.5rem" mb={2}>⚠️</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                Något gick fel
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.88rem', mb: 3, lineHeight: 1.7 }}>{error}</Typography>
              <Box
                onClick={() => navigate('/applab/mockup/new')}
                sx={{ display: 'inline-block', px: 3, py: 1.5, borderRadius: 2, bgcolor: APPLAB_ACCENT, cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', '&:hover': { opacity: 0.9 } }}
              >
                Försök igen
              </Box>
            </>
          ) : (
            <>
              {/* Animated icon */}
              <Box sx={{ fontSize: '3rem', mb: 2, animation: 'pulse 1.8s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }}>
                ✨
              </Box>

              <Typography variant="h5" fontWeight={900} sx={{ mb: 1 }}>
                Bygger din mockup…
              </Typography>
              <Typography sx={{ color: '#666', fontSize: '0.88rem', mb: 4 }}>
                AI:n skapar nu ett komplett HTML-gränssnitt. Det tar vanligtvis 15–30 sekunder.
              </Typography>

              {/* Progress bar */}
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6, borderRadius: 3, mb: 3,
                  bgcolor: 'rgba(255,255,255,0.06)',
                  '& .MuiLinearProgress-bar': { bgcolor: APPLAB_ACCENT, borderRadius: 3 },
                }}
              />

              {/* Step list */}
              <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {STEPS.map((s, i) => {
                  const done    = i < activeStep;
                  const current = i === activeStep;
                  return (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                        bgcolor: done ? APPLAB_ACCENT : current ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${done ? APPLAB_ACCENT : current ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.65rem',
                      }}>
                        {done ? '✓' : ''}
                      </Box>
                      <Typography sx={{
                        fontSize: '0.82rem',
                        color: done ? '#aaa' : current ? 'white' : '#444',
                        fontWeight: current ? 600 : 400,
                        transition: 'color 0.3s',
                      }}>
                        {s.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
